import React, { useState } from "react";
import {
  Modal,
  Tabs,
  InputNumber,
  Input,
  Button,
  Radio,
  Checkbox,
  Typography,
  Tag,
  Divider,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MailOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useNotifications } from "../../contexts/NotificationContext";

const { Text, Title } = Typography;

export default function ThresholdConfigModal({ open, onClose }) {
  const {
    thresholdConfig,
    updateThresholdConfig,
    evaluateThresholdCondition,
  } = useNotifications();

  // Local config form state
  const [threshold, setThreshold] = useState(thresholdConfig.threshold);
  const [repeatOption, setRepeatOption] = useState(thresholdConfig.repeatOption);
  const [emailEnabled, setEmailEnabled] = useState(thresholdConfig.emailEnabled);
  const [inAppEnabled, setInAppEnabled] = useState(thresholdConfig.inAppEnabled);
  const [emailRecipients, setEmailRecipients] = useState(thresholdConfig.emailRecipients);

  // Simulator state
  const [simSpec, setSimSpec] = useState("53-836-00");
  const [simCount, setSimCount] = useState(14);
  const [simModel, setSimModel] = useState("737-MAX");
  const [simResult, setSimResult] = useState(null);

  const handleSaveConfig = () => {
    if (!threshold || threshold < 1) {
      message.error("Threshold must be a valid positive integer (>= 1)");
      return;
    }

    updateThresholdConfig({
      threshold: Number(threshold),
      repeatOption,
      emailEnabled,
      inAppEnabled,
      emailRecipients,
    });
    onClose();
  };

  const runEvaluation = (customCount = simCount) => {
    const count = Number(customCount);
    const thresh = Number(threshold);

    if (count > thresh) {
      setSimResult({
        type: "exceeded",
        title: "THRESHOLD EXCEEDED — CONDITION SATISFIED",
        message: `Defect Count (${count}) > Threshold (${thresh}). Exceeded by +${count - thresh}. System generates Email and In-App notifications.`,
        canDispatch: true,
      });
    } else if (count === thresh) {
      setSimResult({
        type: "equal",
        title: "EQUAL TO THRESHOLD — CONDITION NOT SATISFIED",
        message: `Defect Count (${count}) = Threshold (${thresh}). The requirement strictly mandates 'Defect Count > Threshold'. Therefore, no notification is generated.`,
        canDispatch: false,
      });
    } else {
      setSimResult({
        type: "below",
        title: "BELOW THRESHOLD — NO ACTION",
        message: `Defect Count (${count}) < Threshold (${thresh}). Remaining within safe tolerance. No notification generated.`,
        canDispatch: false,
      });
    }
  };

  const handlePreviewFromSimulator = () => {
    const outcome = evaluateThresholdCondition({ defectCount: simCount });

    if (outcome.satisfied) {
      message.success(
        "Condition satisfied — the backend threshold engine would raise a real alert for this scenario."
      );
    } else {
      message.info(outcome.message);
    }
  };

  // Tab items
  const tabItems = [
    {
      key: "config",
      label: (
        <span>
          <InfoCircleOutlined /> Threshold & Channels
        </span>
      ),
      children: (
        <div>
          {/* Rule banner */}
          <div className="threshold_rule_banner">
            <InfoCircleOutlined className="threshold_rule_banner_icon" />
            <div>
              <Text className="threshold_rule_title">
                Threshold Condition Rule
              </Text>
              <div className="threshold_rule_formula">
                Defect Count &gt; Configured Threshold
              </div>
              <Text className="threshold_rule_desc">
                The trigger condition requires defect count to <strong>strictly exceed</strong> the threshold. A count equal to the threshold (<code>Count = Threshold</code>) does <strong>not</strong> generate notifications.
              </Text>
            </div>
          </div>

          <div className="threshold_form_section">
            <div className="threshold_form_item">
              <Text className="threshold_form_label">
                Configured Defect Threshold (Required)
              </Text>
              <InputNumber
                min={1}
                max={1000}
                value={threshold}
                onChange={(val) => setThreshold(val)}
                style={{ width: 220 }}
                addonAfter="Defects"
              />
              <Text className="threshold_form_hint">
                Threshold count limit across closed-loop feedback metrics.
              </Text>
            </div>

            <div className="threshold_form_item">
              <Text className="threshold_form_label">
                Required Notification Channels
              </Text>
              <div style={{ display: "flex", gap: 20 }}>
                <Checkbox
                  checked={inAppEnabled}
                  onChange={(e) => setInAppEnabled(e.target.checked)}
                >
                  <strong>In-App Notification</strong> (Navbar Bell & Center)
                </Checkbox>
                <Checkbox
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                >
                  <strong>Email Notification</strong> (Automated dispatch)
                </Checkbox>
              </div>
            </div>

            {emailEnabled && (
              <div className="threshold_form_item">
                <Text className="threshold_form_label">
                  Email Notification Recipients
                </Text>
                <Input
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  placeholder="e.g. fleet-reliability@airline.com, techops@airline.com"
                />
                <Text className="threshold_form_hint">
                  Comma-separated list of fleet engineering & reliability team emails.
                </Text>
              </div>
            )}

            <div className="threshold_form_item">
              <Text className="threshold_form_label">
                Repeated Notification Behavior
              </Text>
              <Radio.Group
                value={repeatOption}
                onChange={(e) => setRepeatOption(e.target.value)}
                className="threshold_repeat_radio_group"
              >
                <div
                  className={`threshold_repeat_card ${
                    repeatOption === "A" ? "threshold_repeat_card--selected" : ""
                  }`}
                  onClick={() => setRepeatOption("A")}
                >
                  <Radio value="A">
                    <strong>Option A (Recommended):</strong> Notify only when the count crosses from <code>≤ Threshold → &gt; Threshold</code>
                  </Radio>
                </div>
                <div
                  className={`threshold_repeat_card ${
                    repeatOption === "B" ? "threshold_repeat_card--selected" : ""
                  }`}
                  onClick={() => setRepeatOption("B")}
                >
                  <Radio value="B">
                    <strong>Option B:</strong> Notify every time the defect count changes while remaining above threshold
                  </Radio>
                </div>
                <div
                  className={`threshold_repeat_card ${
                    repeatOption === "C" ? "threshold_repeat_card--selected" : ""
                  }`}
                  onClick={() => setRepeatOption("C")}
                >
                  <Radio value="C">
                    <strong>Option C:</strong> Notify once per relevant defect event
                  </Radio>
                </div>
              </Radio.Group>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "simulator",
      label: (
        <span>
          <ThunderboltOutlined /> Condition Simulator & Sandbox
        </span>
      ),
      children: (
        <div>
          <div className="simulator_box">
            <Text className="simulator_title">
              Interactive Defect Count Rule Tester
            </Text>
            <Text className="threshold_rule_desc" style={{ marginBottom: 12 }}>
              Test any defect count against the active threshold (<strong>{threshold}</strong>) to observe whether an alert is triggered according to the business rules.
            </Text>

            {/* Presets */}
            <div className="simulator_presets">
              <Text strong style={{ fontSize: 12, marginRight: 6, lineHeight: "24px" }}>
                Quick Test Scenarios:
              </Text>
              <Button
                size="small"
                className="simulator_preset_btn"
                onClick={() => {
                  setSimCount(7);
                  runEvaluation(7);
                }}
              >
                Below Threshold (7 vs 10)
              </Button>
              <Button
                size="small"
                className="simulator_preset_btn"
                onClick={() => {
                  setSimCount(10);
                  runEvaluation(10);
                }}
              >
                Equal to Threshold (10 vs 10)
              </Button>
              <Button
                size="small"
                type="primary"
                danger
                className="simulator_preset_btn"
                onClick={() => {
                  setSimCount(11);
                  runEvaluation(11);
                }}
              >
                Exceeds Threshold (11 vs 10)
              </Button>
            </div>

            <div className="simulator_inputs_grid">
              <div>
                <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                  Specification No.
                </Text>
                <Input
                  value={simSpec}
                  onChange={(e) => setSimSpec(e.target.value)}
                />
              </div>
              <div>
                <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                  Aircraft Model
                </Text>
                <Input
                  value={simModel}
                  onChange={(e) => setSimModel(e.target.value)}
                />
              </div>
              <div>
                <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                  Defect Count
                </Text>
                <InputNumber
                  min={0}
                  max={500}
                  value={simCount}
                  onChange={(val) => {
                    setSimCount(val);
                    runEvaluation(val);
                  }}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Evaluation Result */}
            {simResult && (
              <div
                className={`simulator_result_card ${
                  simResult.type === "exceeded"
                    ? "simulator_result_card--exceeded"
                    : simResult.type === "equal"
                    ? "simulator_result_card--equal"
                    : "simulator_result_card--below"
                }`}
              >
                <div className="simulator_result_status">
                  {simResult.type === "exceeded" ? (
                    <ExclamationCircleOutlined style={{ color: "#dc2626" }} />
                  ) : simResult.type === "equal" ? (
                    <InfoCircleOutlined style={{ color: "#16a34a" }} />
                  ) : (
                    <CheckCircleOutlined style={{ color: "#475569" }} />
                  )}
                  <span>{simResult.title}</span>
                </div>
                <div className="simulator_result_desc">{simResult.message}</div>

                {simResult.canDispatch && (
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button
                      type="primary"
                      danger
                      icon={<SendOutlined />}
                      onClick={handlePreviewFromSimulator}
                    >
                      Preview Condition Outcome
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "email_preview",
      label: (
        <span>
          <MailOutlined /> Email Notification Preview
        </span>
      ),
      children: (
        <div className="email_preview_container">
          <div className="email_preview_header">
            <div>
              <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.5 }}>
                SpecOptimizer
              </span>{" "}
              <Tag color="red" style={{ marginLeft: 8 }}>
                CRITICAL THRESHOLD ALERT
              </Tag>
            </div>
            <span style={{ fontSize: 12, opacity: 0.9 }}>
              Southwest Airlines (SWA)
            </span>
          </div>

          <div className="email_preview_body">
            <div className="email_preview_meta">
              <div>
                <strong>To:</strong> {emailRecipients}
              </div>
              <div>
                <strong>Subject:</strong> [ALERT] Defect Threshold Exceeded - Spec 53-836-00 (Count: 14 &gt; Threshold: {threshold})
              </div>
              <div>
                <strong>Timestamp:</strong> {new Date().toUTCString()}
              </div>
            </div>

            <p style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>
              Hello Fleet Engineering Team,
              <br /><br />
              This automated alert is generated because the <strong>closed-loop defect count</strong> for the following maintenance specification has <strong>exceeded the configured operational threshold</strong>.
            </p>

            <table className="email_defect_table">
              <tbody>
                <tr>
                  <th>Specification</th>
                  <td><strong>53-836-00</strong></td>
                  <th>Task Card No</th>
                  <td>753-836-00-01</td>
                </tr>
                <tr>
                  <th>Aircraft Model</th>
                  <td>Boeing 737-MAX</td>
                  <th>Component</th>
                  <td>Fuselage Skin Lap Joint - Station 540</td>
                </tr>
                <tr>
                  <th>Configured Threshold</th>
                  <td><Tag>{threshold} defects</Tag></td>
                  <th>Recorded Defect Count</th>
                  <td><Tag color="red"><strong>14 defects</strong> (+4 Exceeded)</Tag></td>
                </tr>
                <tr>
                  <th>Trigger Condition</th>
                  <td colSpan={3}>
                    <code>Defect Count (14) &gt; Configured Threshold ({threshold})</code>
                  </td>
                </tr>
              </tbody>
            </table>

            <p style={{ fontSize: 12.5, color: "#475569" }}>
              Please review the affected task card in the Closed-Loop Feedback portal to evaluate repeat defect trends and schedule appropriate maintenance inspections.
            </p>

            <a
              href="#view"
              className="email_cta_btn"
              onClick={(e) => {
                e.preventDefault();
                message.info("Email CTA clicked: Direct link to Closed-Loop Feedback portal.");
              }}
            >
              Open Spec in SpecOptimizer &rarr;
            </a>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      title={
        <Title level={4} style={{ margin: 0 }}>
          Closed-Loop Defect Threshold Settings
        </Title>
      }
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSaveConfig}>
          Save Configuration
        </Button>,
      ]}
      className="threshold_modal"
    >
      <Tabs defaultActiveKey="config" items={tabItems} />
    </Modal>
  );
}
